import Dexie from 'dexie';

export const db = new Dexie('DriftDB');

db.version(1).stores({
  sleepLogs: '++id, date, syncStatus, updatedAt',
  factors: '++id, date, syncStatus',
  settings: 'key',
});

// Get sleep log by date
export async function getSleepLogByDate(date) {
  return db.sleepLogs.where('date').equals(date).first();
}

// Get sleep logs for date range
export async function getSleepLogsRange(fromDate, toDate) {
  return db.sleepLogs
    .where('date')
    .between(fromDate, toDate, true, true)
    .sortBy('date');
}

// Get all sleep logs sorted by date descending
export async function getAllSleepLogs() {
  const logs = await db.sleepLogs.toArray();
  return logs.sort((a, b) => b.date.localeCompare(a.date));
}

// Add or update a sleep log
export async function upsertSleepLog(entry) {
  const existing = await getSleepLogByDate(entry.date);
  const now = new Date().toISOString();

  if (existing) {
    await db.sleepLogs.update(existing.id, {
      ...entry,
      updatedAt: now,
      syncStatus: 'pending',
    });
    return { ...existing, ...entry, updatedAt: now };
  }

  const id = await db.sleepLogs.add({
    ...entry,
    createdAt: now,
    updatedAt: now,
    syncStatus: 'local',
  });

  return { id, ...entry, createdAt: now, updatedAt: now };
}

// Delete a sleep log by date
export async function deleteSleepLogByDate(date) {
  const entry = await getSleepLogByDate(date);
  if (entry) {
    await db.sleepLogs.delete(entry.id);
  }
}

// Get factors by date
export async function getFactorsByDate(date) {
  return db.factors.where('date').equals(date).first();
}

// Add or update factors
export async function upsertFactors(entry) {
  const existing = await getFactorsByDate(entry.date);
  if (existing) {
    await db.factors.update(existing.id, { ...entry, syncStatus: 'pending' });
    return { ...existing, ...entry };
  }
  const id = await db.factors.add({ ...entry, syncStatus: 'local' });
  return { id, ...entry };
}

// Get a setting
export async function getSetting(key) {
  const setting = await db.settings.get(key);
  return setting?.value;
}

// Set a setting
export async function setSetting(key, value) {
  await db.settings.put({ key, value });
}

// Get entry count
export async function getEntryCount() {
  return db.sleepLogs.count();
}

// Get streak info
export async function getStreakInfo() {
  const logs = await getAllSleepLogs();
  if (logs.length === 0) return { current: 0, longest: 0 };

  let current = 0;
  let longest = 0;
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateSet = new Set(logs.map(l => l.date));

  // Check from today backwards
  for (let i = 0; i <= logs.length + 30; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() - i);
    const dateStr = checkDate.toISOString().split('T')[0];

    if (dateSet.has(dateStr)) {
      streak++;
      if (streak > longest) longest = streak;
    } else {
      if (i === 0) {
        // Today hasn't been logged yet - that's ok, check yesterday
        continue;
      }
      if (current === 0) current = streak;
      break;
    }
  }

  if (current === 0) current = streak;
  return { current, longest };
}
